import {
  Component,
  ChangeDetectionStrategy,
  contentChild,
  TemplateRef,
  ElementRef,
  input,
  inject,
  viewChild,
  PLATFORM_ID,
  computed,
  signal,
  DestroyRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, timer } from 'rxjs';
import { debounce, distinctUntilChanged, filter, map } from 'rxjs/operators';
import clsx from 'clsx';
import { NgTemplateOutlet } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import type { Options } from '@popperjs/core';

@Component({
  selector: 'pxw-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgbTooltip, NgTemplateOutlet],
  host: {
    '[class]': 'hostClasses()',
  },
})
export class TooltipComponent {
  // Injections
  private readonly elRef = inject(ElementRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  // Signal Inputs
  readonly text = input<string>();
  readonly openDelay = input<number>(1000);
  readonly closeDelay = input<number>(150);
  readonly offsetY = input<number>(10);
  readonly disabled = input<boolean>(false);

  // Content & View Children como signals
  readonly tooltipContent = contentChild<TemplateRef<any>>('tooltipContent');
  readonly ngbTooltipInstance = viewChild<NgbTooltip>(NgbTooltip);

  // State signals
  private readonly tooltipVisibilityTrigger$ = new Subject<{
    visible: boolean;
    skipDebounce: boolean;
  }>();
  private readonly scrollListenerActive = signal(false);

  // Computed signals
  readonly popperOptionsWithOffset = computed<(options: Partial<Options>) => Partial<Options>>(
    () => {
      const offsetY = this.offsetY();
      return (options: Partial<Options>) => ({
        ...options,
        modifiers: [
          ...(options.modifiers || []),
          {
            name: 'offset',
            options: {
              offset: [0, offsetY],
            },
          },
        ],
      });
    },
  );

  readonly hostClasses = computed(() =>
    clsx('ui-tooltip', {
      'ui-tooltip--disabled': this.disabled(),
    }),
  );

  constructor() {
    if (!this.isBrowser) {
      return;
    }

    // Setup event listeners
    this.setupEventListeners();

    // Setup tooltip visibility handling con RxJS
    this.setupTooltipVisibility();
  }

  private setupEventListeners(): void {
    const nativeElement = this.elRef.nativeElement;

    nativeElement.addEventListener('click', this.onMouseClickDisplay);
    nativeElement.addEventListener('mouseenter', this.onMouseEnter);
    nativeElement.addEventListener('mouseleave', this.onMouseLeave);

    // Cleanup en destroy
    this.destroyRef.onDestroy(() => {
      nativeElement.removeEventListener('click', this.onMouseClickDisplay);
      nativeElement.removeEventListener('mouseenter', this.onMouseEnter);
      nativeElement.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeScrollListener();
    });
  }

  private setupTooltipVisibility(): void {
    this.tooltipVisibilityTrigger$
      .pipe(
        filter(({ visible }) => {
          // Si está disabled y queremos abrir, no hacer nada
          return !(visible && this.disabled());
        }),
        debounce(({ visible, skipDebounce }) => {
          if (skipDebounce) {
            return timer(0);
          }
          return timer(visible ? this.openDelay() : this.closeDelay());
        }),
        map(({ visible }) => visible),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(visible => {
        const tooltipInstance = this.ngbTooltipInstance();
        if (tooltipInstance) {
          if (visible) {
            tooltipInstance.open();
            this.addScrollListener();
          } else {
            tooltipInstance.close();
            this.removeScrollListener();
          }
        }
      });
  }

  onShown() {
    // Agregar listeners al tooltip cuando se muestra
    const tooltipElement = document.querySelector('.tooltip');
    if (tooltipElement) {
      tooltipElement.addEventListener('click', this.onMouseClickHide);
      tooltipElement.addEventListener('mouseenter', this.onMouseEnter);
      tooltipElement.addEventListener('mouseleave', this.onMouseLeave);
    }
  }

  onHidden() {
    // Remover listeners del tooltip cuando se oculta
    const tooltipElement = document.querySelector('.tooltip');
    if (tooltipElement) {
      tooltipElement.removeEventListener('click', this.onMouseClickHide);
      tooltipElement.removeEventListener('mouseenter', this.onMouseEnter);
      tooltipElement.removeEventListener('mouseleave', this.onMouseLeave);
    }
  }

  onMouseClickDisplay = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    this.tooltipVisibilityTrigger$.next({ visible: true, skipDebounce: true });
  };

  onMouseClickHide = () => {
    this.tooltipVisibilityTrigger$.next({ visible: false, skipDebounce: true });
  };

  onMouseEnter = () => {
    this.tooltipVisibilityTrigger$.next({ visible: true, skipDebounce: false });
  };

  onMouseLeave = () => {
    this.tooltipVisibilityTrigger$.next({ visible: false, skipDebounce: false });
  };

  onScroll = () => {
    // Cerrar tooltip inmediatamente al hacer scroll
    const tooltipInstance = this.ngbTooltipInstance();
    if (tooltipInstance && tooltipInstance.isOpen()) {
      this.tooltipVisibilityTrigger$.next({ visible: false, skipDebounce: true });
    }
  };

  private addScrollListener() {
    if (!this.isBrowser || this.scrollListenerActive()) {
      return;
    }
    window.addEventListener('scroll', this.onScroll, true);
    this.scrollListenerActive.set(true);
  }

  private removeScrollListener() {
    if (!this.isBrowser || !this.scrollListenerActive()) {
      return;
    }
    window.removeEventListener('scroll', this.onScroll, true);
    this.scrollListenerActive.set(false);
  }
}
